import {ViewContext} from '../view-context/view-context.models';
import type {RecipePreview} from '../../shared/ui/recipe-preview/recipe-preview.models';

/**
 * What this client is actually capable of executing. Sent on every turn.
 * `query_stock`/`product_question` were dropped — they never gated anything server-side.
 */
export const DIALOG_CAPABILITIES: readonly string[] = [
  'remove_stock',
  'add_stock',
  'add_to_shopping_list',
  'create_recipe',
];

/** `'voice'` is type-level only for now — no voice client path exists yet. */
export type DialogChannel = 'text' | 'voice';

/**
 * Per-tool client configuration for a turn, keyed by backend tool name. Each value
 * is an opaque blob the named tool interprets itself — the sibling of `capabilities`
 * (which declares *which* writes this surface allows; this declares *how* a tool
 * should behave here). The one consumer today is `propose_recipe`, which accepts
 * `pantry` ('prefer' | 'auto' | 'ignore') and `preferences` ('apply' | 'ignore').
 * Omitting a tool (or the whole field) lets the backend apply its own defaults.
 */
export type DialogToolProperties = Record<string, Record<string, unknown>>;

export interface DialogTurnRequest {
  utterance: string;
  /** Opaque, backend-owned. null on the first turn, otherwise echoed back verbatim. */
  context: string | null;
  session_id: string;
  channel: DialogChannel;
  capabilities: readonly string[];
  locale: string;
  view_context?: ViewContext;
  tool_properties?: DialogToolProperties;
}

/**
 * Not a closed set — the backend can introduce new states at any time. Unrecognized
 * values must render (`message`) and end the session cleanly via `expects_input`,
 * never wedge it. `state` is for rendering only; lifecycle is driven by `expects_input`.
 */
export type DialogState =
  | 'clarify'
  | 'confirm'
  | 'completed'
  | 'failed'
  /** propose_recipe registered a background authoring job (recipe_job is set) instead
   *  of returning a draft inline — the "recipe" role model takes up to ~40s, far past
   *  any turn's own budget. See RecipeJobRef and RecipeAuthorJobService. */
  | 'recipe_authoring'
  | (string & {});

/**
 * Not a closed set — the backend can introduce new question types at any time.
 * Unrecognized values must be treated like 'free_text' by consumers.
 */
export type ClarifyQuestionType =
  | 'product'
  | 'quantity'
  | 'date'
  | 'location'
  | 'free_text'
  | (string & {});

export interface ClarifyOption {
  index: number;
  id?: number;
  label: string;
  thumb_url?: string;
}

export interface DialogClarify {
  question_type: ClarifyQuestionType;
  slot: string;
  /** `[]` (not omitted) for a plain free-text question with no candidates to pick from. */
  options: ClarifyOption[];
}

/**
 * `type` is intentionally an open string, not a closed union — the backend can
 * introduce new action types at any time. Unknown types must degrade gracefully.
 */
export interface DialogAction {
  type: string;
  payload: Record<string, unknown>;
  executed: boolean;
  requires_confirmation: boolean;
  idempotency_key: string;
}

/**
 * Not a closed set — meaning is defined by `resource` (product ids for `stock`, list
 * ids for `shopping_list`, recipe ids for `recipe`). An unrecognized `resource` must be
 * ignored, not errored on.
 */
export type DialogAffectedResource = 'stock' | 'shopping_list' | 'recipe' | (string & {});

export interface DialogAffected {
  resource: DialogAffectedResource;
  entity_ids: number[];
}

export type DialogFailureReason =
  | 'no_match'
  | 'not_understood'
  | 'turn_limit'
  | 'cancelled'
  | 'unsupported_capability'
  | 'execution_failed';

export interface DialogFailure {
  reason: DialogFailureReason;
}

/** Present only on a `recipe_authoring` turn — the job id to poll via
 *  RecipeAuthorJobService, mirroring the URL importer's own job-polling flow. */
export interface DialogRecipeJobRef {
  job_id: string;
}

/**
 * Shape of `action.payload` when `action.type === 'create_recipe'`. This is exactly
 * the app-neutral `RecipePreview` the shared `ui-recipe-preview` card renders — the
 * canonical shape now lives in `shared/ui/recipe-preview` so the assistant, the
 * recipe importer, and any future surface all render from one definition. Re-exported
 * here under the `RecipeDraft*` names existing dialog callers already use.
 *
 * Not enforced by the wire types above (`DialogAction.payload` stays an untyped
 * `Record<string, unknown>` since payload shape is defined by `action.type`) — callers
 * that branch on `type === 'create_recipe'` cast into this shape themselves. `step.text`
 * carries model-authored `<t k="…">…</t>` tags; never render it as HTML (see
 * `parseRecipeStepText`).
 */
export type {
  RecipePreview as RecipeDraftPayload,
  RecipePreviewStep as RecipeDraftStep,
  RecipePreviewStepType as RecipeDraftStepType,
  RecipeCookDevice,
  RecipeCookMode,
  RecipeWaitLocation,
} from '../../shared/ui/recipe-preview/recipe-preview.models';

/**
 * One structured, client-renderable result a read tool surfaced this turn (see the
 * backend `dialog.TurnArtifact`). `type` discriminates how to render `data`; unknown
 * types must be ignored, never errored on.
 */
export interface DialogTurnArtifact {
  type: 'recipe_suggestions' | 'recipe_matches' | (string & {});
  /** Shape depends on `type` — narrow via `type` before use (see the *ArtifactData types). */
  data: unknown;
}

/** One brainstorm idea from `get_recipe_suggestions` (artifact type `recipe_suggestions`). */
export interface RecipeSuggestionItem {
  title: string;
  description?: string;
  uses_from_stock?: string[];
  missing_ingredients?: string[];
}
export interface RecipeSuggestionsArtifactData {
  suggestions: RecipeSuggestionItem[];
}

/** One saved-recipe match from `search_recipes` (artifact type `recipe_matches`).
 *  `rating_count === 0` means unrated — `rating_average` is meaningless then. */
export interface RecipeMatchItem {
  recipe_id: number;
  title: string;
  servings?: number;
  prep_time_minutes?: number;
  total_time_minutes?: number;
  rating_average?: number;
  rating_count: number;
}
export interface RecipeMatchesArtifactData {
  recipes: RecipeMatchItem[];
}

export interface DialogTurnResponse {
  state: DialogState;
  message: string;
  expects_input: boolean;
  context: string | null;
  clarify?: DialogClarify;
  /** Absent or explicitly `null` for intents with no client action (`query_stock`, `product_question`). */
  action?: DialogAction | null;
  failure?: DialogFailure;
  /** Present on `completed` writes. Tells the client exactly what to refresh. */
  affected?: DialogAffected[];
  /** Present only when state === 'recipe_authoring'. */
  recipe_job?: DialogRecipeJobRef;
  /** Structured results the read tools surfaced this turn (recipe ideas, saved-recipe
   *  matches), rendered alongside `message`. Absent when the turn produced none. */
  artifacts?: DialogTurnArtifact[];
}

export type ChatRole = 'user' | 'assistant' | 'system' | 'error';

/** Raw per-turn wire state, captured only while `DialogDebugService.active()`. */
export interface ChatMessageDebug {
  state: DialogState;
  expectsInput: boolean;
  context: string | null;
  action?: DialogAction | null;
  failure?: DialogFailure;
  affected?: DialogAffected[];
  recipe_job?: DialogRecipeJobRef;
  latencyMs: number;
  /** Full wire request/response for this turn — shown in the turn detail overlay. */
  request: DialogTurnRequest;
  response: DialogTurnResponse;
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  createdAt: number;
  /** True while this assistant message is a pending "typing" placeholder. */
  pending?: boolean;
  /** Attached to the assistant message that carried a clarify turn. */
  clarify?: DialogClarify;
  /** Set once the user has picked a clarify option, so the UI can collapse it. */
  selectedOptionIndex?: number;
  /** True for the assistant message that carried a `confirm` turn. */
  confirm?: boolean;
  /** Set once the user has answered a confirm question, so the UI can collapse it. */
  confirmAnswer?: 'yes' | 'no';
  /** This turn's `action`, if any — kept outside `debug` (unlike the rest of the raw
   *  wire turn) because action-specific rendering (e.g. the recipe draft card, the
   *  "open recipe" link once executed) must work regardless of debug mode. */
  action?: DialogAction | null;
  /** This turn's `affected` resources, if any — same rationale as `action` above. */
  affected?: DialogAffected[];
  /** True for a failed-send user message that can be retried. */
  retryable?: boolean;
  /** Only set while debug mode is active — see `ChatMessageDebug`. */
  debug?: ChatMessageDebug;
  /** Structured results surfaced by the read tools on this turn (recipe ideas from
   *  `get_recipe_suggestions`, saved-recipe matches from `search_recipes`) — rendered
   *  as cards under the message text. Kept outside `debug` like `action`/`affected`
   *  because the rendering must work regardless of debug mode. */
  artifacts?: DialogTurnArtifact[];
  /** Set on the assistant message for a `recipe_authoring` turn — this message's own
   *  view of that job's progress, updated in place as polling advances it. There is no
   *  `PendingAction`/confirm behind this: `draft_ready` renders the same
   *  `ui-recipe-preview` card as a `create_recipe` action, but with its own save button
   *  that calls `RecipeAuthorJobService.saveJob` directly. */
  recipeJob?: ChatMessageRecipeJob;
}

export interface ChatMessageRecipeJob {
  jobId: string;
  status: 'running' | 'draft_ready' | 'succeeded' | 'failed' | (string & {});
  draft?: RecipePreview;
  createdRecipeId?: number;
  errorMessage?: string;
}
